import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Table,
  InputNumber,
  Space,
  Alert,
  message,
  Row,
  Col,
  Divider,
  Card,
  Statistic
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  JournalEntry,
  JournalEntryLine,
  CreateJournalEntryInput,
  Journal,
  journalService
} from '@/services/journalService';
import { Account } from '@/services/accountService';

const { TextArea } = Input;

interface JournalEntryFormProps {
  entry?: JournalEntry;
  journals: Journal[];
  accounts: Account[];
  onSubmit: (values: CreateJournalEntryInput) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

interface EntryLineWithKey extends Omit<JournalEntryLine, 'id'> {
  key: string;
}

const JournalEntryForm: React.FC<JournalEntryFormProps> = ({
  entry,
  journals,
  accounts,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [lines, setLines] = useState<EntryLineWithKey[]>([]);
  const [validationError, setValidationError] = useState<string>('');
  const [totalDebit, setTotalDebit] = useState<number>(0);
  const [totalCredit, setTotalCredit] = useState<number>(0);

  // Initialize form with entry data if editing
  useEffect(() => {
    if (entry) {
      form.setFieldsValue({
        journalId: entry.journalId,
        date: dayjs(entry.date),
        ref: entry.ref,
        narration: entry.narration
      });

      const initialLines: EntryLineWithKey[] = entry.lines.map((line, index) => ({
        key: `line-${index}`,
        accountId: line.accountId,
        name: line.name,
        debit: Number(line.debit),
        credit: Number(line.credit),
        partnerId: line.partnerId,
        currencyId: line.currencyId,
        amountCurrency: line.amountCurrency ? Number(line.amountCurrency) : undefined,
        analyticAccountId: line.analyticAccountId
      }));

      setLines(initialLines);
    } else {
      // Initialize with 2 empty lines for new entry
      addLine();
      addLine();
    }
  }, [entry, form]);

  // Calculate totals whenever lines change
  useEffect(() => {
    const debitTotal = lines.reduce((sum, line) => sum + Number(line.debit || 0), 0);
    const creditTotal = lines.reduce((sum, line) => sum + Number(line.credit || 0), 0);
    setTotalDebit(debitTotal);
    setTotalCredit(creditTotal);

    // Only validate if at least one line has an account selected
    const touchedLines = lines.filter(line => line.accountId);
    if (touchedLines.length > 0) {
      // Validate only touched lines
      const validation = journalService.validateDoubleEntry(touchedLines as JournalEntryLine[]);
      if (!validation.valid) {
        setValidationError(validation.message || '');
      } else {
        setValidationError('');
      }
    } else {
      // Clear error if no lines have been touched yet
      setValidationError('');
    }
  }, [lines]);

  const addLine = () => {
    const newLine: EntryLineWithKey = {
      key: `line-${Date.now()}`,
      accountId: '',
      name: '',
      debit: 0,
      credit: 0
    };
    setLines([...lines, newLine]);
  };

  const removeLine = (key: string) => {
    if (lines.length <= 2) {
      message.warning('Journal entry must have at least 2 lines');
      return;
    }
    setLines(lines.filter(line => line.key !== key));
  };

  const updateLine = (key: string, field: string, value: any) => {
    setLines(lines.map(line => {
      if (line.key === key) {
        // If updating debit, clear credit and vice versa
        if (field === 'debit' && value > 0) {
          return { ...line, debit: value, credit: 0 };
        } else if (field === 'credit' && value > 0) {
          return { ...line, credit: value, debit: 0 };
        }
        return { ...line, [field]: value };
      }
      return line;
    }));
  };

  const handleSubmit = async () => {
    try {
      // Validate form fields
      const values = await form.validateFields();

      // Validate lines
      if (lines.length < 2) {
        message.error('Journal entry must have at least 2 lines');
        return;
      }

      // Validate all lines have account selected
      const invalidLines = lines.filter(line => !line.accountId);
      if (invalidLines.length > 0) {
        message.error('All lines must have an account selected');
        return;
      }

      // Validate double-entry
      const validation = journalService.validateDoubleEntry(lines as JournalEntryLine[]);
      if (!validation.valid) {
        message.error(validation.message);
        return;
      }

      // Prepare submission data
      const submitData: CreateJournalEntryInput = {
        journalId: values.journalId,
        date: values.date.toISOString(),
        ref: values.ref,
        narration: values.narration,
        lines: lines.map(line => ({
          accountId: line.accountId,
          name: line.name,
          debit: Number(line.debit),
          credit: Number(line.credit),
          partnerId: line.partnerId,
          currencyId: line.currencyId,
          amountCurrency: line.amountCurrency,
          analyticAccountId: line.analyticAccountId
        }))
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const getDifference = () => {
    return Math.abs(totalDebit - totalCredit);
  };

  const isBalanced = () => {
    return getDifference() < 0.01;
  };

  const columns = [
    {
      title: 'Account',
      dataIndex: 'accountId',
      key: 'accountId',
      width: 250,
      render: (accountId: string, record: EntryLineWithKey) => (
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Select account"
          value={accountId || undefined}
          onChange={(value) => updateLine(record.key, 'accountId', value)}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={accounts
            .filter(acc => !acc.deprecated)
            .map(acc => ({
              value: acc.id,
              label: `${acc.code} - ${acc.name}`
            }))}
        />
      )
    },
    {
      title: 'Description',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name: string, record: EntryLineWithKey) => (
        <Input
          value={name || ''}
          onChange={(e) => updateLine(record.key, 'name', e.target.value)}
          placeholder="Line description"
        />
      )
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      width: 150,
      align: 'right' as const,
      render: (debit: number, record: EntryLineWithKey) => (
        <InputNumber
          style={{ width: '100%' }}
          value={debit}
          onChange={(value) => updateLine(record.key, 'debit', value || 0)}
          min={0}
          precision={2}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
        />
      )
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      width: 150,
      align: 'right' as const,
      render: (credit: number, record: EntryLineWithKey) => (
        <InputNumber
          style={{ width: '100%' }}
          value={credit}
          onChange={(value) => updateLine(record.key, 'credit', value || 0)}
          min={0}
          precision={2}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
        />
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: EntryLineWithKey) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeLine(record.key)}
          disabled={lines.length <= 2}
        />
      )
    }
  ];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          date: dayjs()
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="journalId"
              label="Journal"
              rules={[{ required: true, message: 'Please select a journal' }]}
            >
              <Select
                showSearch
                placeholder="Select journal"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={journals
                  .filter(j => j.active)
                  .map(j => ({
                    value: j.id,
                    label: `${j.code} - ${j.name}`
                  }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="date"
              label="Entry Date"
              rules={[{ required: true, message: 'Please select a date' }]}
            >
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="ref" label="Reference">
              <Input placeholder="External reference (e.g., invoice number)" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="narration" label="Description">
          <TextArea
            rows={3}
            placeholder="Enter description of this journal entry"
          />
        </Form.Item>
      </Form>

      <Divider orientation="left">Entry Lines</Divider>

      <Table
        dataSource={lines}
        columns={columns}
        pagination={false}
        size="small"
        rowKey="key"
        footer={() => (
          <div>
            <Button
              type="dashed"
              onClick={addLine}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              Add Line
            </Button>
          </div>
        )}
      />

      <Card style={{ marginTop: 16, background: '#fafafa' }}>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="Total Debit"
              value={totalDebit}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Total Credit"
              value={totalCredit}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Difference"
              value={getDifference()}
              precision={2}
              prefix="$"
              valueStyle={{ color: isBalanced() ? '#3f8600' : '#cf1322' }}
            />
          </Col>
        </Row>

        {validationError && (
          <Alert
            message="Validation Error"
            description={validationError}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        {!validationError && isBalanced() && lines.length >= 2 && (
          <Alert
            message="Entry Balanced"
            description="Debit equals Credit - Ready to save"
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          <Button onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSubmit}
            loading={loading}
            disabled={!isBalanced() || validationError !== '' || lines.length < 2}
          >
            Save as Draft
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default JournalEntryForm;